import { PROTOCOL_VERSION } from '../constants';
import { clamp } from '../math';
import { readOp, VoxelMask, writeOp, type VoxelOp } from '../voxel/ops';
import { BinaryReader, BinaryWriter } from './codec';

// Message ids ---------------------------------------------------------------------------

export const C2S = {
  Hello: 1,
  Input: 2,
  Chat: 3,
  Ping: 4,
  ChunkHashes: 5,
  Loadout: 6,
  Command: 7,
} as const;

export const S2C = {
  Welcome: 1,
  Chunks: 2,
  ChunksDone: 3,
  Tick: 4,
  PlayerInfo: 5,
  PlayerLeft: 6,
  Pong: 7,
  MatchEnd: 8,
  Kick: 9,
  Scoreboard: 10,
} as const;

/** Input button bits. */
export const Btn = {
  Jump: 1,
  Fire: 2,
  Aim: 4,
  Reload: 8,
  Gadget: 16,
  Jet: 32,
  Use: 64,
} as const;

// Position quantization: 1/256 m, 0..256 m.
export const qpos = (v: number): number => clamp(Math.round(v * 256), 0, 65535);
export const dqpos = (q: number): number => q / 256;

// Client -> server -----------------------------------------------------------------------

export interface InputFrame {
  seq: number;
  /** Quantized strafe/forward axes (-127..127). */
  mx: number;
  mz: number;
  /** Quantized yaw (u16) and pitch (i16). */
  yaw: number;
  pitch: number;
  buttons: number;
  slot: number;
  /** Server tick (with 1/256 fraction) the client was displaying when this input was sampled. */
  viewTick: number;
  viewFrac: number;
}

export function encodeHello(ticket: string, clientVersion: string, w = new BinaryWriter(256)): Uint8Array {
  return w.reset().u8(C2S.Hello).u16(PROTOCOL_VERSION).string(ticket).string(clientVersion).finish();
}

export function encodeInputs(frames: InputFrame[], w = new BinaryWriter(256)): Uint8Array {
  w.reset().u8(C2S.Input).u8(frames.length);
  for (const f of frames) {
    w.u32(f.seq).i8(f.mx).i8(f.mz).u16(f.yaw).i16(f.pitch).u16(f.buttons).u8(f.slot).u32(f.viewTick).u8(f.viewFrac);
  }
  return w.finish();
}

export function decodeInputs(r: BinaryReader): InputFrame[] {
  const n = r.u8();
  const out: InputFrame[] = [];
  for (let i = 0; i < n; i++) {
    out.push({
      seq: r.u32(),
      mx: r.i8(),
      mz: r.i8(),
      yaw: r.u16(),
      pitch: r.i16(),
      buttons: r.u16(),
      slot: r.u8(),
      viewTick: r.u32(),
      viewFrac: r.u8(),
    });
  }
  return out;
}

export function encodeChat(text: string): Uint8Array {
  return new BinaryWriter(128).u8(C2S.Chat).string(text.slice(0, 160)).finish();
}

/** Client ping carries its send time and the last measured round trip (ms) for scoreboards. */
export function encodePing(time: number, rttMs: number): Uint8Array {
  return new BinaryWriter(16).u8(C2S.Ping).f64(time).u16(Math.min(65535, Math.round(rttMs))).finish();
}

export function encodeChunkHashes(entries: [number, number][]): Uint8Array {
  const w = new BinaryWriter(8 + entries.length * 8).u8(C2S.ChunkHashes).varint(entries.length);
  for (const [ci, h] of entries) w.varint(ci).u32(h);
  return w.finish();
}

/** Loadout/cosmetic change requests and misc commands travel as JSON. */
export function encodeJsonMsg(type: number, value: unknown): Uint8Array {
  return new BinaryWriter(256).u8(type).string(JSON.stringify(value)).finish();
}

// Server -> client ------------------------------------------------------------------------

export interface FallingSnapshot {
  id: number;
  mask: VoxelMask;
  values: Uint8Array;
  /** Distance already fallen in meters, and current speed. */
  offset: number;
  velocity: number;
}

export interface WelcomeMsg {
  playerId: number;
  serverTick: number;
  mapSeed: number;
  mapHash: number;
  /** JSON: { matchId, settings, modeState, players, serverTime } */
  json: string;
  falling: FallingSnapshot[];
}

export function encodeWelcome(m: WelcomeMsg): Uint8Array {
  const w = new BinaryWriter(4096);
  w.u8(S2C.Welcome).u8(m.playerId).u32(m.serverTick).u32(m.mapSeed).u32(m.mapHash).string(m.json);
  w.varint(m.falling.length);
  for (const f of m.falling) {
    w.varint(f.id);
    f.mask.write(w);
    w.blob(f.values);
    w.f32(f.offset).f32(f.velocity);
  }
  return w.finish();
}

export function decodeWelcome(r: BinaryReader): WelcomeMsg {
  const playerId = r.u8();
  const serverTick = r.u32();
  const mapSeed = r.u32();
  const mapHash = r.u32();
  const json = r.string();
  const n = r.varint();
  const falling: FallingSnapshot[] = [];
  for (let i = 0; i < n; i++) {
    const id = r.varint();
    const mask = VoxelMask.read(r);
    const values = r.blob().slice();
    falling.push({ id, mask, values, offset: r.f32(), velocity: r.f32() });
  }
  return { playerId, serverTick, mapSeed, mapHash, json, falling };
}

/** Owner-only state for prediction/reconciliation and HUD. */
export interface OwnState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  onGround: boolean;
  jet: number;
  hp: number;
  maxHp: number;
  armor: number;
  maxArmor: number;
  slot: number;
  /** Bit 1 reloading, 2 charging, 4 aiming. */
  weaponFlags: number;
  /** Seconds left on the current reload. */
  reloadLeft: number;
  ammo: number[];
  reserve: number[];
  /** Weapon num per slot (0 = empty); modes can grant weapons outside the loadout. */
  weapons: number[];
  gadgetCooldown: number;
  gadgetCharges: number;
  /** Gadget num currently held (0 = none); modes can swap it (BR loot, tutorial). */
  gadget: number;
  /** Seconds until respawn when dead. */
  respawnIn: number;
  /** Bits: 1 burning, 2 stim, 4 spawn protected, 8 alive, 16 carrying flag, 32 in zone danger, 64 gliding. */
  status: number;
  /** Builder blocks / special resource. */
  blocks: number;
  /** Equipped pet num (0 = none). */
  pet: number;
  /** Pet health 0..100 (0 while destroyed or absent). */
  petHpPct: number;
  /** Seconds until a destroyed pet re-forms (0 when present). */
  petReformIn: number;
}

export const PlayerFlag = {
  Alive: 1,
  OnGround: 2,
  Firing: 4,
  Reloading: 8,
  Aiming: 16,
  Protected: 32,
  Jet: 64,
  Carrying: 128,
} as const;

export interface PlayerSnap {
  id: number;
  flags: number;
  x: number;
  y: number;
  z: number;
  /** Quantized yaw/pitch. */
  yaw: number;
  pitch: number;
  weapon: number;
  hpPct: number;
  armorPct: number;
}

/** A live pet (pets that are destroyed or whose owner is dead are not sent). */
export interface PetSnap {
  owner: number;
  /** Pet num (content PETS). */
  pet: number;
  state: number;
  x: number;
  y: number;
  z: number;
  /** Quantized yaw. */
  yaw: number;
  hpPct: number;
}

export const PetState = {
  Follow: 0,
  /** Flying at / biting a target (attacker) or chewing voxels (digger). */
  Work: 1,
  Return: 2,
  /** Guard interposing / medic healing / scout pinging (visual emphasis). */
  Active: 3,
} as const;

/** Ev.Pet actions (effects and HUD feedback). */
export const PetAction = {
  Bite: 1,
  Heal: 2,
  /** Scout ping at the pet; `value` = mark duration (tenths of a second). */
  Ping: 3,
  /** An enemy marked by a ping: `target` is the marked player, `value` = duration (tenths). */
  Mark: 4,
  Dig: 5,
  /** Guard absorbed `value` damage aimed at its owner. */
  Absorb: 6,
  /** The pet took `value` damage from `target` (hit markers for the attacker). */
  Hurt: 7,
  Destroyed: 8,
  Reform: 9,
} as const;

export const Ev = {
  Op: 1,
  Shot: 2,
  Hit: 3,
  Kill: 4,
  Spawn: 5,
  ProjSpawn: 6,
  ProjEnd: 7,
  Explosion: 8,
  Chat: 9,
  Mode: 10,
  Announce: 11,
  Pickup: 12,
  Sound: 13,
  Gadget: 14,
  Heal: 15,
  Melee: 16,
  Beam: 17,
  Pet: 18,
} as const;

export const HitFlag = {
  Headshot: 1,
  Explosion: 2,
  Melee: 4,
  Burn: 8,
  Crush: 16,
  Blocked: 32,
  Kill: 64,
} as const;

export const KillFlag = {
  Headshot: 1,
  Explosion: 2,
  Collapse: 4,
  Melee: 8,
  Suicide: 16,
  Zone: 32,
  FirstBlood: 64,
  Revenge: 128,
} as const;

export const ProjEndReason = { Explode: 0, Fizzle: 1, Stick: 2, HitPlayer: 3 } as const;

export type GameEvent =
  | { e: typeof Ev.Op; op: VoxelOp; by: number }
  | { e: typeof Ev.Shot; shooter: number; weapon: number; x: number; y: number; z: number; yaw: number; pitch: number; seed: number }
  | {
      e: typeof Ev.Hit;
      attacker: number;
      victim: number;
      zone: number;
      part: number;
      gx: number;
      gy: number;
      gz: number;
      damage: number;
      armorDamage: number;
      flags: number;
      hpPct: number;
      armorPct: number;
      /** Source position for damage indicators / debris direction. */
      sx: number;
      sy: number;
      sz: number;
    }
  | { e: typeof Ev.Kill; killer: number; victim: number; weapon: number; flags: number; assister: number; streak: number }
  | { e: typeof Ev.Spawn; player: number; x: number; y: number; z: number; yaw: number }
  | { e: typeof Ev.ProjSpawn; id: number; owner: number; weapon: number; tick: number; x: number; y: number; z: number; vx: number; vy: number; vz: number }
  | { e: typeof Ev.ProjEnd; id: number; x: number; y: number; z: number; reason: number }
  | { e: typeof Ev.Explosion; x: number; y: number; z: number; radius: number; kind: number }
  | { e: typeof Ev.Chat; player: number; text: string }
  | { e: typeof Ev.Mode; json: string }
  | { e: typeof Ev.Announce; kind: number; a: number; b: number; text: string }
  | { e: typeof Ev.Pickup; action: number; id: number; kind: number; item: number; x: number; y: number; z: number }
  | { e: typeof Ev.Sound; sound: number; x: number; y: number; z: number }
  | { e: typeof Ev.Gadget; player: number; gadget: number; x: number; y: number; z: number }
  | { e: typeof Ev.Heal; player: number; amount: number; hpPct: number }
  | { e: typeof Ev.Melee; player: number; weapon: number }
  | { e: typeof Ev.Beam; player: number; weapon: number; on: boolean; x: number; y: number; z: number }
  | { e: typeof Ev.Pet; action: number; owner: number; target: number; x: number; y: number; z: number; value: number };

export interface TickMsg {
  tick: number;
  ackSeq: number;
  own: OwnState | null;
  players: PlayerSnap[];
  pets: PetSnap[];
  events: GameEvent[];
}

function writeEvent(w: BinaryWriter, ev: GameEvent): void {
  w.u8(ev.e);
  switch (ev.e) {
    case Ev.Op:
      w.u8(ev.by);
      writeOp(w, ev.op);
      return;
    case Ev.Shot:
      w.u8(ev.shooter).u16(ev.weapon).u16(qpos(ev.x)).u16(qpos(ev.y)).u16(qpos(ev.z)).u16(ev.yaw).i16(ev.pitch).u32(ev.seed);
      return;
    case Ev.Hit:
      w.u8(ev.attacker).u8(ev.victim).u8(ev.zone).u8(ev.part).u8(ev.gx).u8(ev.gy).u8(ev.gz);
      w.u16(Math.min(65535, Math.round(ev.damage))).u16(Math.min(65535, Math.round(ev.armorDamage))).u8(ev.flags).u8(ev.hpPct).u8(ev.armorPct);
      w.u16(qpos(ev.sx)).u16(qpos(ev.sy)).u16(qpos(ev.sz));
      return;
    case Ev.Kill:
      w.u8(ev.killer).u8(ev.victim).u16(ev.weapon).u8(ev.flags).u8(ev.assister).u8(ev.streak);
      return;
    case Ev.Pet:
      w.u8(ev.action).u8(ev.owner).u8(ev.target).u16(qpos(ev.x)).u16(qpos(ev.y)).u16(qpos(ev.z)).u16(Math.max(0, Math.min(65535, Math.round(ev.value))));
      return;
    case Ev.Spawn:
      w.u8(ev.player).u16(qpos(ev.x)).u16(qpos(ev.y)).u16(qpos(ev.z)).u16(ev.yaw);
      return;
    case Ev.ProjSpawn:
      w.u16(ev.id).u8(ev.owner).u16(ev.weapon).u32(ev.tick).f32(ev.x).f32(ev.y).f32(ev.z).f32(ev.vx).f32(ev.vy).f32(ev.vz);
      return;
    case Ev.ProjEnd:
      w.u16(ev.id).u16(qpos(ev.x)).u16(qpos(ev.y)).u16(qpos(ev.z)).u8(ev.reason);
      return;
    case Ev.Explosion:
      w.u16(qpos(ev.x)).u16(qpos(ev.y)).u16(qpos(ev.z)).u8(Math.min(255, Math.round(ev.radius * 10))).u8(ev.kind);
      return;
    case Ev.Chat:
      w.u8(ev.player).string(ev.text);
      return;
    case Ev.Mode:
      w.string(ev.json);
      return;
    case Ev.Announce:
      w.u8(ev.kind).u8(ev.a).u8(ev.b).string(ev.text);
      return;
    case Ev.Pickup:
      w.u8(ev.action).u16(ev.id).u8(ev.kind).u16(ev.item).u16(qpos(ev.x)).u16(qpos(ev.y)).u16(qpos(ev.z));
      return;
    case Ev.Sound:
      w.u8(ev.sound).u16(qpos(ev.x)).u16(qpos(ev.y)).u16(qpos(ev.z));
      return;
    case Ev.Gadget:
      w.u8(ev.player).u8(ev.gadget).u16(qpos(ev.x)).u16(qpos(ev.y)).u16(qpos(ev.z));
      return;
    case Ev.Heal:
      w.u8(ev.player).u16(ev.amount).u8(ev.hpPct);
      return;
    case Ev.Melee:
      w.u8(ev.player).u16(ev.weapon);
      return;
    case Ev.Beam:
      w.u8(ev.player).u16(ev.weapon).bool(ev.on).u16(qpos(ev.x)).u16(qpos(ev.y)).u16(qpos(ev.z));
      return;
  }
}

function readEvent(r: BinaryReader): GameEvent {
  const e = r.u8();
  switch (e) {
    case Ev.Op: {
      const by = r.u8();
      return { e, by, op: readOp(r) };
    }
    case Ev.Shot:
      return { e, shooter: r.u8(), weapon: r.u16(), x: dqpos(r.u16()), y: dqpos(r.u16()), z: dqpos(r.u16()), yaw: r.u16(), pitch: r.i16(), seed: r.u32() };
    case Ev.Hit:
      return {
        e,
        attacker: r.u8(),
        victim: r.u8(),
        zone: r.u8(),
        part: r.u8(),
        gx: r.u8(),
        gy: r.u8(),
        gz: r.u8(),
        damage: r.u16(),
        armorDamage: r.u16(),
        flags: r.u8(),
        hpPct: r.u8(),
        armorPct: r.u8(),
        sx: dqpos(r.u16()),
        sy: dqpos(r.u16()),
        sz: dqpos(r.u16()),
      };
    case Ev.Kill:
      return { e, killer: r.u8(), victim: r.u8(), weapon: r.u16(), flags: r.u8(), assister: r.u8(), streak: r.u8() };
    case Ev.Pet:
      return { e, action: r.u8(), owner: r.u8(), target: r.u8(), x: dqpos(r.u16()), y: dqpos(r.u16()), z: dqpos(r.u16()), value: r.u16() };
    case Ev.Spawn:
      return { e, player: r.u8(), x: dqpos(r.u16()), y: dqpos(r.u16()), z: dqpos(r.u16()), yaw: r.u16() };
    case Ev.ProjSpawn:
      return { e, id: r.u16(), owner: r.u8(), weapon: r.u16(), tick: r.u32(), x: r.f32(), y: r.f32(), z: r.f32(), vx: r.f32(), vy: r.f32(), vz: r.f32() };
    case Ev.ProjEnd:
      return { e, id: r.u16(), x: dqpos(r.u16()), y: dqpos(r.u16()), z: dqpos(r.u16()), reason: r.u8() };
    case Ev.Explosion:
      return { e, x: dqpos(r.u16()), y: dqpos(r.u16()), z: dqpos(r.u16()), radius: r.u8() / 10, kind: r.u8() };
    case Ev.Chat:
      return { e, player: r.u8(), text: r.string() };
    case Ev.Mode:
      return { e, json: r.string() };
    case Ev.Announce:
      return { e, kind: r.u8(), a: r.u8(), b: r.u8(), text: r.string() };
    case Ev.Pickup:
      return { e, action: r.u8(), id: r.u16(), kind: r.u8(), item: r.u16(), x: dqpos(r.u16()), y: dqpos(r.u16()), z: dqpos(r.u16()) };
    case Ev.Sound:
      return { e, sound: r.u8(), x: dqpos(r.u16()), y: dqpos(r.u16()), z: dqpos(r.u16()) };
    case Ev.Gadget:
      return { e, player: r.u8(), gadget: r.u8(), x: dqpos(r.u16()), y: dqpos(r.u16()), z: dqpos(r.u16()) };
    case Ev.Heal:
      return { e, player: r.u8(), amount: r.u16(), hpPct: r.u8() };
    case Ev.Melee:
      return { e, player: r.u8(), weapon: r.u16() };
    case Ev.Beam:
      return { e, player: r.u8(), weapon: r.u16(), on: r.bool(), x: dqpos(r.u16()), y: dqpos(r.u16()), z: dqpos(r.u16()) };
    default:
      throw new Error(`Unknown event ${e}`);
  }
}

/** Events are encoded once per tick and shared by every recipient. */
export function encodeEvents(events: GameEvent[], w = new BinaryWriter(1024)): Uint8Array {
  w.reset().varint(events.length);
  for (const ev of events) writeEvent(w, ev);
  return w.finish();
}

export function encodeTick(
  tick: number,
  ackSeq: number,
  own: OwnState | null,
  players: PlayerSnap[],
  eventBytes: Uint8Array,
  w = new BinaryWriter(2048),
  pets: PetSnap[] = [],
): Uint8Array {
  w.reset().u8(S2C.Tick).u32(tick).u32(ackSeq);
  if (own) {
    w.u8(1);
    w.f32(own.x).f32(own.y).f32(own.z).f32(own.vx).f32(own.vy).f32(own.vz);
    w.bool(own.onGround).f32(own.jet);
    w.u16(Math.max(0, Math.ceil(own.hp))).u16(own.maxHp).u16(Math.max(0, Math.ceil(own.armor))).u16(own.maxArmor);
    w.u8(own.slot).u8(own.weaponFlags).u16(Math.round(own.reloadLeft * 1000));
    w.u8(own.ammo.length);
    for (let i = 0; i < own.ammo.length; i++) w.u16(own.ammo[i]).u16(own.reserve[i]).u16(own.weapons[i] ?? 0);
    w.u16(Math.round(own.gadgetCooldown * 100)).u8(own.gadgetCharges).u8(own.gadget);
    w.u8(Math.min(255, Math.round(own.respawnIn * 10))).u8(own.status).u16(own.blocks);
    w.u8(own.pet).u8(Math.max(0, Math.min(100, Math.round(own.petHpPct)))).u8(Math.min(255, Math.ceil(own.petReformIn * 10)));
  } else {
    w.u8(0);
  }
  w.u8(players.length);
  for (const p of players) {
    w.u8(p.id).u8(p.flags).u16(qpos(p.x)).u16(qpos(p.y)).u16(qpos(p.z)).u16(p.yaw).i16(p.pitch).u16(p.weapon).u8(p.hpPct).u8(p.armorPct);
  }
  w.u8(pets.length);
  for (const p of pets) w.u8(p.owner).u8(p.pet).u8(p.state).u16(qpos(p.x)).u16(qpos(p.y)).u16(qpos(p.z)).u16(p.yaw).u8(p.hpPct);
  w.bytes(eventBytes);
  return w.finish();
}

export function decodeTick(r: BinaryReader): TickMsg {
  const tick = r.u32();
  const ackSeq = r.u32();
  let own: OwnState | null = null;
  if (r.u8() === 1) {
    const x = r.f32();
    const y = r.f32();
    const z = r.f32();
    const vx = r.f32();
    const vy = r.f32();
    const vz = r.f32();
    const onGround = r.bool();
    const jet = r.f32();
    const hp = r.u16();
    const maxHp = r.u16();
    const armor = r.u16();
    const maxArmor = r.u16();
    const slot = r.u8();
    const weaponFlags = r.u8();
    const reloadLeft = r.u16() / 1000;
    const n = r.u8();
    const ammo: number[] = [];
    const reserve: number[] = [];
    const weapons: number[] = [];
    for (let i = 0; i < n; i++) {
      ammo.push(r.u16());
      reserve.push(r.u16());
      weapons.push(r.u16());
    }
    const gadgetCooldown = r.u16() / 100;
    const gadgetCharges = r.u8();
    const gadget = r.u8();
    const respawnIn = r.u8() / 10;
    const status = r.u8();
    const blocks = r.u16();
    const pet = r.u8();
    const petHpPct = r.u8();
    const petReformIn = r.u8() / 10;
    own = { x, y, z, vx, vy, vz, onGround, jet, hp, maxHp, armor, maxArmor, slot, weaponFlags, reloadLeft, ammo, reserve, weapons, gadgetCooldown, gadgetCharges, gadget, respawnIn, status, blocks, pet, petHpPct, petReformIn };
  }
  const np = r.u8();
  const players: PlayerSnap[] = [];
  for (let i = 0; i < np; i++) {
    players.push({ id: r.u8(), flags: r.u8(), x: dqpos(r.u16()), y: dqpos(r.u16()), z: dqpos(r.u16()), yaw: r.u16(), pitch: r.i16(), weapon: r.u16(), hpPct: r.u8(), armorPct: r.u8() });
  }
  const nPets = r.u8();
  const pets: PetSnap[] = [];
  for (let i = 0; i < nPets; i++) {
    pets.push({ owner: r.u8(), pet: r.u8(), state: r.u8(), x: dqpos(r.u16()), y: dqpos(r.u16()), z: dqpos(r.u16()), yaw: r.u16(), hpPct: r.u8() });
  }
  const ne = r.varint();
  const events: GameEvent[] = [];
  for (let i = 0; i < ne; i++) events.push(readEvent(r));
  return { tick, ackSeq, own, players, pets, events };
}

export function decodeEventsOnly(r: BinaryReader): GameEvent[] {
  const ne = r.varint();
  const events: GameEvent[] = [];
  for (let i = 0; i < ne; i++) events.push(readEvent(r));
  return events;
}
